import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Close } from '@mui/icons-material';
import { Autocomplete, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';

import { AgentClient, AgentModel } from '../../api/api';
import useApiClient from '../hooks/useApiClient';

type AgentPickerInputProps = {
	value: unknown;
	onValueChange: (value: unknown) => void;
	variant?: 'standard' | 'outlined' | 'filled';
	expression?: boolean;
	readOnly?: boolean;
};

export default function AgentPickerInput({ value, onValueChange, variant = 'standard', expression = false, readOnly }: AgentPickerInputProps) {
	const client = useApiClient(AgentClient);
	const [focused, setFocused] = useState(false);
	const [inputText, setInputText] = useState('');
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const currentId = typeof value === 'string'
		? value.trim()
		: value && typeof value === 'object' && 'Value' in value && typeof value.Value === 'string'
			? value.Value.trim()
			: '';
	const normalizedCurrentId = currentId.toLocaleLowerCase();

	useEffect(() => {
		const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
		return () => window.clearTimeout(timeout);
	}, [search]);

	const { data: agents = [], isFetching, isError } = useQuery({
		queryKey: ['agent-picker', debouncedSearch],
		queryFn: async () => {
			const page = await client.getAgents(0, 20, debouncedSearch || undefined);
			return page.items ?? [];
		},
		enabled: focused,
	});
	const { data: selectedAgent } = useQuery({
		queryKey: ['agent-picker-by-id', normalizedCurrentId],
		queryFn: () => client.getAgent(currentId),
		enabled: currentId.length > 0,
		retry: false,
		staleTime: 60_000,
	});
	const selectedFromList = agents.find((agent) => agent.id.toLocaleLowerCase() === normalizedCurrentId);
	const selected = selectedFromList ?? selectedAgent;

	useEffect(() => {
		if (!focused) {
			setInputText(selectedAgent?.name ?? '');
		}
	}, [currentId, focused, selectedAgent?.name]);

	const selectAgent = (agent: AgentModel | null) => {
		if (agent) {
			onValueChange(expression ? { Type: 'Const', ValueType: 'Guid', Value: agent.id } : agent.id);
			setInputText(agent.name);
			setSearch('');
		}
	};

	return (
		<Autocomplete
			options={agents}
			value={selected}
			inputValue={inputText}
			loading={isFetching}
			open={focused}
			disableClearable
			readOnly={readOnly}
			getOptionLabel={(agent) => agent.name}
			isOptionEqualToValue={(option, selectedAgent) => option.id.toLocaleLowerCase() === selectedAgent.id.toLocaleLowerCase()}
			filterOptions={(options) => options}
			noOptionsText={isError ? 'Could not load agents' : search ? 'No matching agents' : 'No agents available'}
			onFocus={() => setFocused(true)}
			onClose={() => setFocused(false)}
			onInputChange={(_event, nextValue, reason) => {
				if (reason === 'input') {
					setInputText(nextValue);
					setSearch(nextValue);
				}
				else if (reason === 'clear') {
					setInputText('');
					setSearch('');
				}
			}}
			onChange={(_event, agent) => selectAgent(agent)}
			renderInput={(params) => (
				<TextField
					{...params}
					variant={variant}
					placeholder="Search agents"
					fullWidth
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{currentId && !readOnly && (
									<InputAdornment position="end">
										<Tooltip title="Clear agent">
										<IconButton size="small" aria-label="Clear agent" onMouseDown={(event) => event.preventDefault()} onClick={() => { onValueChange(null); setInputText(''); setSearch(''); }}>
												<Close fontSize="small" />
											</IconButton>
										</Tooltip>
									</InputAdornment>
								)}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
		/>
	);
}
